import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/components/Toast';
import { ROUTES } from '@/shared/constants/routes';
import { PLAN_REQUIRED_EVENT, type PlanRequiredDetail } from '@/shared/services/api';
import { useAccountState } from '@/shared/hooks/useAccountState';

/**
 * Global safety net for plan-gated API errors. The axios interceptor
 * broadcasts `unipostudio:plan-required` whenever any request fails with
 * PLAN_REQUIRED (deduped there). This listener turns it into a visible
 * upgrade toast with a one-click path to the Subscription tab — so a plan
 * error can never fail silently, even from a component with no error
 * handling of its own.
 *
 * The toast names the plan that opens the feature when the API said which,
 * in the reader's language. The server's own message is English and is
 * never shown.
 *
 * Mounted once in main.tsx inside ToastProvider + Router.
 */
export function PlanRequiredListener(): null {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Only the account owner changes the plan; a member's link leads to who manages billing.
  const member = useAccountState().isAccountOwner === false;

  useEffect(() => {
    const onPlanRequired = (e: Event): void => {
      const requiredPlan = (e as CustomEvent<Partial<PlanRequiredDetail> | undefined>).detail?.requiredPlan ?? null;
      const message = requiredPlan
        ? t('toast.planRequiredNamed', { plan: t(`overview.plans.${requiredPlan}`) })
        : t('toast.planRequired');
      toast('warning', message, 7000, {
        label: member ? t('account.billingLink') : t('toast.upgrade'),
        onClick: () => navigate(ROUTES.SETTINGS + '?tab=subscription'),
      });
    };
    window.addEventListener(PLAN_REQUIRED_EVENT, onPlanRequired);
    return () => window.removeEventListener(PLAN_REQUIRED_EVENT, onPlanRequired);
  }, [toast, navigate, t, member]);

  return null;
}
